import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

import {LocalAssembly} from '../BulkAssemblyConstants';
import {detectHomology} from '../BulkAssemblyDAO';
import {getNextSlotIdx, getPreviousSlotIdx, getSlotName, slotHasSequences} from '../BulkAssemblyUtil';

// Make sure the upper bounds do not exceed constant in benchling.bulk_workflows.assembly.homology_detection
export const UPPER_BOUNDS = [3000, 2500, 2000, 1500, 1000, 500, 100];
const DEFAULT_UPPER_REGION_BOUND: number = 3000;

interface Props {
  assembly: LocalAssembly;
  onRequestClose: () => void;
  onRequestSubmit: (region: string | null) => void;
  slotIdx: number;
}

type HomologyDetectionErrorMessage = string | null;

const getUniqueNeighbors = (
  assembly: LocalAssembly,
  prevSlotIdx: number | null,
  nextSlotIdx: number | null
): Array<number> =>
  Array.from(
    new Set(
      [
        slotHasSequences(assembly, prevSlotIdx) && prevSlotIdx,
        slotHasSequences(assembly, nextSlotIdx) && nextSlotIdx,
      ].filter((idx): idx is number => idx !== false)
    )
  );

const HomologyDetectionModal = (props: Props): JSX.Element => {
  const {assembly, onRequestClose, onRequestSubmit, slotIdx} = props;
  const [maxLength, setMaxLength] = useState<number>(DEFAULT_UPPER_REGION_BOUND);
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyTried, setAlreadyTried] = useState(false);
  const [homologyRegions, setHomologyRegions] = useState<Array<string>>([]);
  const [selectedHomologyRegion, setSelectedHomologyRegion] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<HomologyDetectionErrorMessage>(null);

  const prevSlotIdx = getPreviousSlotIdx(assembly, slotIdx);
  const nextSlotIdx = getNextSlotIdx(assembly, slotIdx);
  const uniqueNeighbors = getUniqueNeighbors(assembly, prevSlotIdx, nextSlotIdx);
  const defaultNeighborSlotIdx = uniqueNeighbors[0];
  const [neighborSlotIdx, setNeighborSlotIdx] = useState<number | null>(defaultNeighborSlotIdx);

  const handleDetectHomology = (): void => {
    setIsLoading(true);
    setErrorMessage(null);
    detectHomology(assembly, {
      slot1Index: slotIdx,
      slot2Index: neighborSlotIdx!,
      maxRegionLength: maxLength,
    })
      .then(({bases, basesSecondary}) => {
        const newHomologyRegions = [];
        if (bases) {
          newHomologyRegions.push(bases);
        }
        if (basesSecondary) {
          newHomologyRegions.push(basesSecondary);
        }
        if (newHomologyRegions.length === 1) {
          setSelectedHomologyRegion(newHomologyRegions[0]);
        } else {
          setSelectedHomologyRegion(null);
        }
        setHomologyRegions(newHomologyRegions);
      })
      .catch((err) => {
        setErrorMessage(err.userMessage);
      })
      .finally(() => {
        setIsLoading(false);
        if (alreadyTried) {
          deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.RETRIED_HOMOLOGY_DETECTION);
        } else {
          setAlreadyTried(true);
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleDetectHomology, [assembly, maxLength, neighborSlotIdx, slotIdx]);

  return (
    <Modal
      title="Detect Homology Region"
      onRequestSubmit={() => {
        onRequestSubmit(selectedHomologyRegion);
      }}
      onRequestClose={onRequestClose}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          disabled: false,
        },
        {
          text: 'Select',
          submit: true,
          disabled:
            // eslint-disable-next-line benchlint/no-eqeqeq-null
            errorMessage != null || isLoading || neighborSlotIdx === null || selectedHomologyRegion == null,
          dataTestAttrs: {
            component: 'HomologyDetectionModal',
            element: 'footerButton',
            key: ModalButtonKey.SUBMIT,
          },
        },
      ]}
      dataTestAttrs={{component: 'HomologyDetectionModal', element: 'root'}}
    >
      {uniqueNeighbors.length > 1 && (
        <div className="u-flex-display u-vertical-flex u-spacing-bottom">
          <FieldLabel className="u-small-spacing-bottom" label="Target Slot" />
          <ToggleGroup<number>
            options={[
              {id: prevSlotIdx!, buttonProps: {text: getSlotName(assembly, prevSlotIdx!)}},
              {id: nextSlotIdx!, buttonProps: {text: getSlotName(assembly, nextSlotIdx!)}},
            ]}
            selection={neighborSlotIdx!}
            onChangeSelection={setNeighborSlotIdx}
            size={ButtonSize.MEDIUM}
          />
        </div>
      )}
      <div className="u-flex-display u-vertical-flex u-spacing-bottom">
        <FieldLabel className="u-small-spacing-bottom" label="Homology Region Length" />
        <div className={css(styles.range)}>
          <SelectDropdown
            disabled={isLoading}
            displayName={`Under ${maxLength} bases`}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={maxLength}>
                {UPPER_BOUNDS.map((bound) => (
                  <Option key={bound} value={bound} onSelect={setMaxLength}>
                    {`Under ${bound} bases`}
                  </Option>
                ))}
              </Menu>
            }
          />
        </div>
      </div>
      <hr />
      {isLoading ? (
        <HorizontalThrobber />
      ) : errorMessage != null ? (
        <ErrorMessage error={errorMessage} />
      ) : homologyRegions.length > 0 ? (
        <RadioButtonGroup<string>
          options={homologyRegions.map((homologyRegion) => ({
            label: <span className={css(styles.span)}>{homologyRegion}</span>,
            value: homologyRegion,
          }))}
          onChange={setSelectedHomologyRegion}
          selectedValue={selectedHomologyRegion}
          title="Result(s)"
          isVertical={true}
        />
      ) : (
        'No valid homology regions found.'
      )}
    </Modal>
  );
};

export default HomologyDetectionModal;

const styles = StyleSheet.create({
  range: {
    display: 'inline',
  },
  numericInput: {
    width: '50px',
  },
  span: {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
});
